import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

const HQ = { name: "Belfast (HQ)", coords: [54.5973, -5.9301] };
const CITIES = [
  { name: "Lisburn", coords: [54.5162, -6.0581] },
  { name: "Bangor", coords: [54.6538, -5.6688] },
  { name: "Newtownabbey", coords: [54.6789, -5.9026] },
  { name: "Carrickfergus", coords: [54.7157, -5.8067] },
  { name: "Antrim", coords: [54.7195, -6.2085] },
  { name: "Ballymena", coords: [54.8641, -6.2761] },
  { name: "Newry", coords: [54.1751, -6.3402] },
  { name: "Derry / Londonderry", coords: [55.0071, -7.3247] },
  { name: "Armagh", coords: [54.3499, -6.6531] },
  { name: "Enniskillen", coords: [54.3438, -7.6335] },
  { name: "Dublin", coords: [53.3498, -6.2603] },
  { name: "Glasgow", coords: [55.8642, -4.2518] },
  { name: "Edinburgh", coords: [55.9533, -3.1883] },
  { name: "Manchester", coords: [53.4808, -2.2426] },
  { name: "Liverpool", coords: [53.4084, -2.9916] },
  { name: "Birmingham", coords: [52.4862, -1.8904] },
  { name: "London", coords: [51.5072, -0.1276] },
  { name: "Cardiff", coords: [51.4816, -3.1791] },
];

export default function Coverage() {
  const mapRef = useRef(null);
  const mapInstance = useRef(null);
  const routesRef = useRef([]);
  const markersRef = useRef([]);
  const [activeIdx, setActiveIdx] = useState(0);

  // Init map once
  useEffect(() => {
    if (!mapRef.current || mapInstance.current) return;

    const map = L.map(mapRef.current, {
      scrollWheelZoom: false,
      zoomControl: true,
    });
    mapInstance.current = map;

    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
    }).addTo(map);

    // HQ marker
    L.circleMarker(HQ.coords, {
      color: "#C05C3A",
      fillColor: "#C05C3A",
      fillOpacity: 1,
      radius: 10,
    })
      .addTo(map)
      .bindPopup("<b>Jaybliss Logistics HQ</b><br/>Belfast, Northern Ireland");

    // City markers + routes
    CITIES.forEach((c, i) => {
      const route = L.polyline([HQ.coords, c.coords], {
        color: "#0B1E3F",
        weight: 1,
        opacity: 0.35,
        dashArray: "3, 6",
      }).addTo(map);
      routesRef.current[i] = route;

      const m = L.circleMarker(c.coords, {
        color: "#0B1E3F",
        fillColor: "#0B1E3F",
        fillOpacity: 1,
        radius: 4,
      })
        .addTo(map)
        .bindPopup(`<b>${c.name}</b>`);
      m.on("click", () => setActiveIdx(i));
      markersRef.current[i] = m;
    });

    const all = [HQ.coords, ...CITIES.map((c) => c.coords)];
    map.fitBounds(all, { padding: [40, 40] });

    return () => {
      map.remove();
      mapInstance.current = null;
      routesRef.current = [];
      markersRef.current = [];
    };
  }, []);

  // Highlight active route/marker
  useEffect(() => {
    routesRef.current.forEach((route, i) => {
      if (!route) return;
      const active = i === activeIdx;
      route.setStyle({
        color: active ? "#C05C3A" : "#0B1E3F",
        weight: active ? 3 : 1,
        opacity: active ? 0.95 : 0.35,
        dashArray: active ? null : "3, 6",
      });
    });
    markersRef.current.forEach((m, i) => {
      if (!m) return;
      const active = i === activeIdx;
      m.setStyle({
        color: active ? "#C05C3A" : "#0B1E3F",
        fillColor: active ? "#C05C3A" : "#0B1E3F",
        radius: active ? 7 : 4,
      });
    });
  }, [activeIdx]);

  // Rotate active route
  useEffect(() => {
    const t = setInterval(() => {
      setActiveIdx((prev) => (prev + 1) % CITIES.length);
    }, 1600);
    return () => clearInterval(t);
  }, []);

  return (
    <section id="coverage" className="relative py-24 md:py-32 bg-[var(--jb-bone)]" data-testid="coverage-section">
      <div className="max-w-[1440px] mx-auto px-6 md:px-10">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="grid md:grid-cols-12 gap-10 mb-14"
        >
          <div className="md:col-span-1 mono-cap text-[var(--jb-rust)]">(Coverage)</div>
          <h2 className="md:col-span-8 serif text-[clamp(40px,6vw,88px)] leading-[0.98] tracking-tight text-[var(--jb-navy)]">
            Belfast-based. <span className="italic">Serving the whole UK.</span>
          </h2>
          <p className="md:col-span-3 text-[15px] text-[var(--jb-muted)] leading-[1.6]">
            Local moves, cross-country relocations and everything in between. Wherever you're going, we've got a route.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-12 gap-6">
          <div className="md:col-span-8 relative aspect-[4/3] md:aspect-[16/10] overflow-hidden border border-[var(--jb-line)]" data-testid="coverage-map" data-lenis-prevent>
            <div ref={mapRef} className="absolute inset-0" />
            <div className="absolute top-4 left-4 bg-[var(--jb-navy)] text-[var(--jb-bone)] px-3 py-1.5 mono-cap z-[500] pointer-events-none">
              Live Coverage
            </div>
          </div>

          <div className="md:col-span-4 flex flex-col">
            <div className="mono-cap text-[var(--jb-muted)] mb-4">Active route</div>
            <div className="border-l-2 border-[var(--jb-rust)] pl-5 mb-6">
              <div className="mono-cap text-[var(--jb-muted)] text-[10px]">Belfast HQ →</div>
              <div className="serif text-3xl md:text-4xl text-[var(--jb-navy)] leading-tight mt-1 min-h-[3rem]" data-testid="active-city">
                {CITIES[activeIdx].name}
              </div>
            </div>
            <div className="mono-cap text-[var(--jb-muted)] mb-3">All areas served</div>
            <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-[13px] max-h-[280px] overflow-auto pr-2">
              {CITIES.map((c, i) => (
                <button
                  key={c.name}
                  onClick={() => setActiveIdx(i)}
                  className={`text-left flex items-center gap-2 transition-colors ${
                    activeIdx === i ? "text-[var(--jb-rust)]" : "text-[var(--jb-navy)]"
                  }`}
                >
                  <span
                    className={`w-1.5 h-1.5 rounded-full ${
                      activeIdx === i ? "bg-[var(--jb-rust)]" : "bg-[var(--jb-navy)]/40"
                    }`}
                  />
                  {c.name}
                </button>
              ))}
            </div>
            <div className="hairline my-6" />
            <p className="text-[12px] text-[var(--jb-muted)] leading-[1.6]">
              Don't see your postcode? We move anywhere in the UK & Republic of Ireland. Send us your route and we'll price it.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
